import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const AuthContext = createContext(null);

const PWD_ACK_KEY = "ayobell.password_ack";

function announcePasswordChange(u) {
  if (!u?.password_updated_at) return;
  const acknowledged = localStorage.getItem(PWD_ACK_KEY);
  if (acknowledged === u.password_updated_at) return; // already acknowledged this change

  // First-ever record for this user (no prior ack) → set silently so we don't nag on every fresh login
  if (!acknowledged) {
    localStorage.setItem(PWD_ACK_KEY, u.password_updated_at);
    return;
  }

  // Password has been changed since last ack — surface it once. Delay slightly so
  // it stacks after the login "Welcome back" toast rather than being replaced.
  try {
    const when = new Date(u.password_updated_at).toLocaleString(undefined, {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
    setTimeout(() => {
      toast.success(`Password changed on ${when}. If this wasn't you, contact your administrator.`, {
        duration: 10000,
        description: "For your security, we're letting you know your credentials were updated.",
      });
    }, 1200);
  } catch { /* toast is best-effort */ }
  localStorage.setItem(PWD_ACK_KEY, u.password_updated_at);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("smartcare_token");
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      announcePasswordChange(data);
    } catch (_) {
      localStorage.removeItem("smartcare_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("smartcare_token", data.token);
    setUser(data.user);
    announcePasswordChange(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("smartcare_token");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
